import React from 'react';
import { CheckIcon } from 'lucide-react';
import { ScreenShell, Headline, Subtext } from '../components/ScreenShell';
import { PrimaryButton } from '../components/ui/PrimaryButton';
import { adaptLabel, childWord } from '../utils/gender';
import type { PersonalizationProfile } from '../types/profile';
interface SummaryScreenProps {
  profile: PersonalizationProfile;
  customInterest: string;
  submitting: boolean;
  error: string;
  onConsentChange: (value: boolean) => void;
  onSubmit: () => void;
  onEdit: () => void;
}
function SummaryRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="border-b border-lavender-100 py-3.5 last:border-none" data-id="element-165" data-mp="1ed05h4ngcqj3">
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-166" data-mp="19ma19uqritpz">{label}</p>
      <p className="mt-1 text-[15px] leading-snug text-ink" data-id="element-167" data-mp="lwfmh7mvuv33">{value}</p>
    </div>;
}
export function SummaryScreen({
  profile,
  customInterest,
  submitting,
  error,
  onConsentChange,
  onSubmit,
  onEdit
}: SummaryScreenProps) {
  const interests = profile.child_interests.map(item => item === 'Algo diferente' && customInterest ? customInterest : item).join(', ');
  const personality = profile.child_personality.map(item => adaptLabel(item, profile.child_gender)).join(', ');
  return <ScreenShell data-id="element-168" data-mp="3npbmyu5dc3nk">
      <Headline data-id="element-169" data-mp="3nns7h5rjdwmu">Conocimos un poquito mejor a {profile.child_name} 💜</Headline>

      <div className="mt-6 rounded-4xl border border-lavender-200 bg-white/90 px-5 py-1 shadow-soft" data-id="element-170" data-mp="2qzkuf0gn0qke">
        <SummaryRow label="Hijo o hija" value={childWord(profile.child_gender).replace(/^./, c => c.toUpperCase())} data-id="element-171" data-mp="2vx49u3p2n9dx" />
        <SummaryRow label="Edad" value={`${profile.child_age} años`} data-id="element-172" data-mp="301lqvv41r8l0" />
        <SummaryRow label="Intereses" value={interests} data-id="element-173" data-mp="34zdjysgfh0qs" />
        <SummaryRow label="Personalidad" value={personality} data-id="element-174" data-mp="f9igl84r48k8" />
        <SummaryRow label="Fortalezas a trabajar" value={profile.development_priorities.join(', ')} data-id="element-175" data-mp="fbctq09s6wdr" />
      </div>

      <Subtext data-id="element-176" data-mp="2gayhuyi5ozch">
        Con esta información podremos crear experiencias que se sientan realmente hechas para{' '}
        {profile.child_name}.
      </Subtext>

      <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-3xl border border-lavender-200 bg-lavender-50 p-4" data-id="element-177" data-mp="2cbz54rq6nyah">
        <span className="relative mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center" data-id="element-178" data-mp="2laue2bkm3mhh">
          <input type="checkbox" checked={profile.parental_consent} onChange={event => onConsentChange(event.target.checked)} className="peer h-6 w-6 appearance-none rounded-lg border border-lavender-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-violet-400/25" data-id="element-179" data-mp="1xkqlcz8yp759" />
          <CheckIcon aria-hidden="true" className="pointer-events-none absolute h-4 w-4 text-white opacity-0 peer-checked:opacity-100" data-id="element-180" data-mp="1euij0jd36kq4" />
        </span>
        <span className="text-[13px] leading-relaxed text-ink-soft" data-id="element-181" data-mp="19tl3yfndi6su">
          Confirmo que soy padre, madre o tutor responsable y autorizo el uso de esta información
          para crear el producto personalizado que compré.
        </span>
      </label>

      {error && <p role="alert" className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600" data-id="element-182" data-mp="3cjxjiv2v8mgk">
          {error}
        </p>}

      <div className="mt-6 space-y-1" data-id="element-183" data-mp="83v011onmnch">
        <PrimaryButton onClick={onSubmit} disabled={!profile.parental_consent} loading={submitting} data-id="element-184" data-mp="84xrpbtswttg">
          {submitting ? 'Enviando…' : `Crear el programa de ${profile.child_name} ✨`}
        </PrimaryButton>
        <PrimaryButton variant="ghost" onClick={onEdit} disabled={submitting} data-id="element-185" data-mp="3vawzx7waony8">
          Modificar respuestas
        </PrimaryButton>
      </div>
    </ScreenShell>;
}